import React, {useCallback, useEffect} from 'react';
import {useHistory} from "react-router";
import {useCustomerStore} from "../../redux/customers";
import {useAuthStore} from "../../redux/auth";
import {useSlotsStore} from "../../redux/slots";
import {useProfilePackageStore} from "../../redux/profile-packages";
import {useBookingsStore} from "../../redux/bookings";
import {useHolidaysStore} from "../../redux/holidays";
import {useCommunicationsStore} from "../../redux/communications";
import {useWaitingBookingsStore} from "../../redux/waiting-bookings";
import {useProfilesStore} from "../../redux/profiles";
import {useChatsStore} from "../../redux/chats";
import {useFinancialTransactionStore} from "../../redux/financial-transactions";
import {useOrderStore} from "../../redux/orders";
import {useUpcomingEventsStore} from "../../redux/upcoming-events";
import {usePreferencesFacade} from "../../redux/preferences";
import {clearCache} from "../../services/api/responseCache";
import {getDeviceUuid} from "../../utils/deviceUuid";

interface Props {
    mustLogout: boolean,
    mustLogoutWithoutApi: boolean,
    setMustLogout: (newMustLogout: boolean) => void,
}

const LogoutHandler: React.FC<Props> = props => {
    const {mustLogout, setMustLogout, mustLogoutWithoutApi} = props;
    const history = useHistory();
    const customerStore = useCustomerStore();
    const authStore = useAuthStore();
    const chatsStore = useChatsStore();
    const profilesStore = useProfilesStore();
    const financialTransactionStore = useFinancialTransactionStore();
    const profilePackageStore = useProfilePackageStore();
    const slotsStore = useSlotsStore();
    const waitingBookingsStore = useWaitingBookingsStore();
    const bookingsStore = useBookingsStore();
    const communicationsStore = useCommunicationsStore();
    const holidaysStore = useHolidaysStore();
    const orderStore = useOrderStore();
    const upcomingEventsStore = useUpcomingEventsStore();
    const preferencesStore = usePreferencesFacade();

    const {clearAll: communicationsClearAll} = communicationsStore;
    const {clearAll: profilesClearAll} = profilesStore;
    const {clearAll: chatsClearAll} = chatsStore;
    const {clearAll: financialTransactionsClearAll} = financialTransactionStore;
    const {clearAll: bookingsClearAll} = bookingsStore;
    const {clearAll: waitingBookingsClearAll} = waitingBookingsStore;
    const {clearAll: holidaysClearAll} = holidaysStore;
    const {clearAll: slotsClearAll} = slotsStore;
    const {clearAll: customerClearAll} = customerStore;
    const {clearAll: ppClearAll} = profilePackageStore;
    const {clearAll: ordersClearAll} = orderStore;
    const {clearAll: upcomingEventsClearAll} = upcomingEventsStore;
    const {clearAll: preferencesClearAll} = preferencesStore;
    const {isLoading: authIsLoading, isLoggingOut, isAuthed, logout: apiLogout, clearAll: authClearAll} = authStore;

    const clearAllAndRedirect = useCallback(() => {
        // Drop the in-memory response cache: it's keyed by URL only and survives
        // logout (no reload on native), so without this the next user is served
        // this user's cached /customers, /financial-transactions, etc. (F1).
        clearCache();
        Promise.all([
            slotsClearAll(),
            bookingsClearAll(),
            waitingBookingsClearAll(),
            profilesClearAll(),
            chatsClearAll(),
            financialTransactionsClearAll(),
            holidaysClearAll(),
            communicationsClearAll(),
            customerClearAll(),
            ppClearAll(),
            ordersClearAll(),
            upcomingEventsClearAll(),
            preferencesClearAll(),
            authClearAll(),
        ]).then(() => {
            history.replace('/auth/login');
        }).catch(() => {
            console.error('Error while clearing local storage');
        });
    }, [history, slotsClearAll, bookingsClearAll, waitingBookingsClearAll, profilesClearAll, chatsClearAll, financialTransactionsClearAll, communicationsClearAll, holidaysClearAll, customerClearAll, ppClearAll, ordersClearAll, upcomingEventsClearAll, preferencesClearAll, authClearAll])

    const logout = useCallback(() => {
        setMustLogout(false);
        if (isAuthed){
            // Resolve the device identity fresh and pass it straight through —
            // never via React state, which an already-created closure reads as
            // undefined and the API then skips FCM deregistration (F6). The
            // helper returns the same uuid push registration used per platform,
            // so the API can actually match the token row (F7).
            getDeviceUuid()
                .catch((error) => {
                    console.error('Could not resolve the device uuid for logout', error);
                    return undefined;
                })
                .then((deviceUuid) => apiLogout({deviceUuid}))
                .finally(() => {
                    clearAllAndRedirect();
                });
        } else {
            clearAllAndRedirect();
        }
    }, [apiLogout, clearAllAndRedirect, setMustLogout, isAuthed]);

    useEffect(() => {
        if (mustLogout) {
            logout();
        }
        else if(!isAuthed && (authIsLoading || isLoggingOut)){
            clearAllAndRedirect();
        }
    }, [mustLogout])

    useEffect(() => {
        if (mustLogoutWithoutApi) {
            clearAllAndRedirect();
        }
    }, [mustLogoutWithoutApi])

    return null;
};

export default LogoutHandler;
